import { Component, HostListener, OnInit, PLATFORM_ID, computed, inject, OnDestroy, signal, ChangeDetectionStrategy, forwardRef } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { Papa } from 'ngx-papaparse';

import {
  CODEMIRROR_TYPE_MATCH,
  FILE_TYPE,
  ResponseType,
} from '../../constants/general.constants';
import { GeneralHelpers } from '../../helpers/general.helper';
import { Dialog } from '../../interfaces/create-dialog.interface';
import { ProjectFileItem, NotebookFile } from '../../interfaces/file.interface';
import { FileService } from '../../services/file/file.service';
import { CacheService } from '../../services/cache/cache.service';
import { SnapshotService } from '../../services/snapshot/snapshot.service';
import { ToastrService } from 'ngx-toastr';
import { tableFromIPC } from 'apache-arrow';
import { from, fromArrow } from 'arquero';
import { PermissionId } from '../../constants/permissions.constants';
import { ProjectService } from '../../services/project/project.service';
import { NotebookDefaultFilesService } from '../../services/notebook-default-file/notebook-default-file.service';
import { AppStateService } from '../../services/app-state/app-state.service';
import { AutoUnsubscribe } from '../../decorators/auto-unsubscribe.decorator';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { PipesModule } from '../../pipes/pipes.module';
import { CodeEditorComponent } from '../code-editor/code-editor.component';
import { TableComponent } from '../table/table.component';
import { ArqueroTable } from '../../interfaces/arquero-table.interface';

// Layout constants used by computeEditorMaxHeight() to cap the embedded
// code editor so its internal scroller handles overflow instead of the
// dialog body. The dialog itself caps at DIALOG_VIEWPORT_FRACTION of the
// viewport height; DIALOG_CHROME_HEIGHT_PX is reserved for the header,
// toolbars, file metadata, and action buttons. EDITOR_MIN_HEIGHT_PX is
// the documented floor for the editor, and EDITOR_FALLBACK_HEIGHT_PX is
// used when the host environment doesn't expose window.innerHeight
// (SSR / non-browser).
const DIALOG_VIEWPORT_FRACTION = 0.8;
const DIALOG_CHROME_HEIGHT_PX = 240;
const EDITOR_MIN_HEIGHT_PX = 300;
const EDITOR_FALLBACK_HEIGHT_PX = 600;

/**
 * File types the template has a @case for. Anything else falls through to the
 * @default "preview is not available" message, so there is no reason to fetch
 * its content. Keep in sync with the @switch in watch-file-dialog.component.html.
 */
const PREVIEWABLE_FILE_TYPES = new Set<FILE_TYPE>([
  FILE_TYPE.image,
  FILE_TYPE.video,
  FILE_TYPE.audio,
  FILE_TYPE.csv,
  FILE_TYPE.arrow,
  FILE_TYPE.md,
  FILE_TYPE.javascript,
  FILE_TYPE.python,
  FILE_TYPE.text,
]);

@AutoUnsubscribe()
@Component({
  standalone: true,
  selector: 'app-watch-file-dialog',
  templateUrl: './watch-file-dialog.component.html',
  styleUrls: ['./watch-file-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    // Material
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatCardModule,
    // Layout
    FlexLayoutModule,
    // Code editor — forwardRef breaks an NG0919 circular import: this dialog is
    // opened by DialogService, and CodeEditorComponent injects DialogService,
    // so the direct reference is undefined when this dialog's component def is
    // built, crashing the dialog on open (SCRUM-718, follow-up to SCRUM-689).
    forwardRef(() => CodeEditorComponent),
    // Pipes
    PipesModule,
    // Table — TableComponent also injects DialogService; same NG0919 cycle.
    forwardRef(() => TableComponent),
  ],
})
export class WatchFileDialogComponent implements OnInit, OnDestroy {
  data = inject<Dialog>(MAT_DIALOG_DATA);
  private fileService = inject(FileService);
  private cacheService = inject(CacheService);
  private snapshotService = inject(SnapshotService);
  private notebookDefaultFilesService = inject(NotebookDefaultFilesService);
  private toastr = inject(ToastrService);
  private readonly papa = inject(Papa);
  #projectService = inject(ProjectService);
  #appState = inject(AppStateService);

  hasWritePermission = computed(() =>
    this.#projectService.getCurrentUserHasProjectPermission(PermissionId.WRITE)
  );

  public fileItem!: ProjectFileItem | NotebookFile | any;
  public fileType!: string;
  public mode: string | any = null;
  public sizeFormatted!: string;
  public loading = signal(false);
  public base64 = signal<any>('');
  public tableData = signal<any>(undefined);

  private platformId = inject(PLATFORM_ID);
  // Caps the embedded code editor height so its internal scroller handles
  // overflow instead of the dialog body, avoiding nested scrollbars.
  // See the module-level layout constants for the cap derivation.
  public editorMaxHeight = signal(this.computeEditorMaxHeight());

  ngOnInit() {
    this.processFileData();
    this.setFileType();
    this.setMode();
  }

  ngOnDestroy(): void {
    // Subscriptions are handled by @AutoUnsubscribe() decorator
    // No manual cleanup needed
    void 0; // No-op to satisfy linter
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.editorMaxHeight.set(this.computeEditorMaxHeight());
  }

  private computeEditorMaxHeight(): number {
    if (!isPlatformBrowser(this.platformId) || typeof window === 'undefined') {
      return EDITOR_FALLBACK_HEIGHT_PX;
    }
    return Math.max(
      EDITOR_MIN_HEIGHT_PX,
      Math.floor(window.innerHeight * DIALOG_VIEWPORT_FRACTION) - DIALOG_CHROME_HEIGHT_PX,
    );
  }

  private processFileData() {
    this.fileItem = this.data?.dialogData;

    if (this.fileItem === undefined) {
      return;
    }

    this.sizeFormatted = GeneralHelpers.formatBytes(this.fileItem.filesize);

    // Nothing outside PREVIEWABLE_FILE_TYPES has a @case in the template, so
    // fetching one only to land on the @default "preview is not available"
    // message downloads (and, for Blob types, base64-encodes) the whole file
    // for nothing. Most visible on PDFs, which lost their preview in EHQ-171.
    if (!PREVIEWABLE_FILE_TYPES.has(this.fileItem.fileType as FILE_TYPE)) {
      return;
    }

    const responseType = GeneralHelpers.getResponseType(this.fileItem.fileType);

    this.getById(responseType);
  }

  private getById(responseType?: string) {
    // Snapshot files (from snapshots)
    if (this.fileItem && 'snapshotId' in this.fileItem && !!this.fileItem.snapshotId) {
      this.getSnapshotFile(responseType);
      return;
    }
    // Notebook default files
    if (this.isDefaultFile(this.fileItem)) {
      this.getDefaultFile(responseType);
      return;
    }
    // Notebook cache files
    if (this.fileItem && 'cacheId' in this.fileItem && this.fileItem.cacheId !== undefined) {
      this.getNotebookFile(responseType);
      return;
    }
    // Otherwise treat as Project file (flat project version files)
    this.getProjectFile(responseType);
  }

  private isDefaultFile(file: NotebookFile): boolean {
    return 'isDefaultFile' in file && (file as any).isDefaultFile === true;
  }

  private async getDefaultFile(responseType?: string) {
    if (
      !this.fileItem.fileId ||
      !this.#appState.projectId() ||
      !this.#appState.workflowId()
    ) {
      this.toastr.error('Missing required file information');
      return;
    }

    this.notebookDefaultFilesService
      .getDefaultFileContents(
        this.#appState.projectId(),
        this.#appState.workflowId(),
        this.fileItem.fileId.toString(),
        responseType as any
      )
      .subscribe({
        next: (data: any) => {
          this.processFileCallback(data, responseType);
        },
        error: (_error) => {
          this.toastr.error('Failed to load default file content');
        },
      });
  }

  private async getNotebookFile(responseType?: string) {
    this.cacheService
      .getCacheContentFile(`${this.fileItem.fileId}`, responseType as any)
      .subscribe({
        next: (data: any) => {
          this.processFileCallback(data, responseType);
        },
        error: () => {
          this.toastr.error('Failed to load cache file content');
        },
      });
  }

  private async getSnapshotFile(responseType?: string) {
    this.snapshotService
      .getSnapshotFile(
        this.fileItem.snapshotId,
        `${this.fileItem.fileId}`,
        responseType as any
      )
      .subscribe({
        next: (data: any) => {
          this.processFileCallback(data, responseType);
        },
        error: () => {
          this.toastr.error('Failed to load snapshot file content');
        },
      });
  }

  private processFileCallback(data: any, responseType?: string) {
    if (responseType === ResponseType.Blob) {
      this.processBlobData(data);
    } else if (
      this.fileItem.fileType === FILE_TYPE.csv &&
      responseType === ResponseType.Text
    ) {
      this.parseCsvFile(data);
    } else if (this.fileItem.fileType === FILE_TYPE.arrow) {
      this.processArrowData(data);
    } else {
      this.data.model = data;
    }
  }

  private convertToArqueroTable(data: any): any {
    const tableData = data instanceof ArrayBuffer ? new Uint8Array(data) : data;
    try {
      const arrowTable = tableFromIPC(tableData);
      return fromArrow(arrowTable as any);
    } catch (_error) {
      return null;
    }
  }

  private convertArqueroToJson(arqueroTable: any): any {
    try {
      return arqueroTable.toJSON();
    } catch (_error) {
      return null;
    }
  }

  private convertArqueroToCsv(arqueroTable: any): any {
    try {
      return this.papa.parse(arqueroTable.toCSV()).data;
    } catch (_error) {
      this.toastr.error('Error Arrow file is corrupted');
      return null;
    }
  }

  private processTableJson(tableJson: any): void {
    if (GeneralHelpers.isJSON(tableJson)) {
      const tableData = GeneralHelpers.jsonParse(tableJson);
      this.tableData.set(GeneralHelpers.prepareTableFromArqueroJSON(tableData as ArqueroTable));
      return;
    }

    if (Array.isArray(tableJson) && tableJson.length > 0) {
      const arqueroTable = from(tableJson).toJSON();
      const tableData = GeneralHelpers.jsonParse(arqueroTable);
      this.tableData.set(GeneralHelpers.prepareTableFromArqueroJSON(tableData as ArqueroTable));
      return;
    }

    this.tableData.set(tableJson);
  }

  private async processArrowData(data: any) {
    const arqueroTable = this.convertToArqueroTable(data);
    if (!arqueroTable) {
      return;
    }

    let tableJson = this.convertArqueroToJson(arqueroTable);
    if (!tableJson) {
      tableJson = this.convertArqueroToCsv(arqueroTable);
    }

    if (tableJson) {
      this.processTableJson(tableJson);
    }
  }

  private getProjectFile(responseType?: string) {
    const fileId = Number(this.fileItem.fileId);
    const projectId = Number(this.fileItem?.projectId) || this.#appState.projectId();
    const versionId = Number(this.fileItem?.versionId) || this.#appState.versionId();

    if (!projectId || !versionId || !fileId) {
      this.toastr.error('Missing required file information');
      return;
    }

    this.fileService
      .getProjectFileContentForVersion(projectId, versionId, fileId, responseType as any)
      .subscribe({
        next: (data: any) => {
          this.processFileCallback(data, responseType);
        },
        error: () => { },
      });
  }

  private processBlobData(data: any) {
    // Ensure data is a Blob before processing
    const blob = data instanceof Blob ? data : new Blob([data], { 
      type: GeneralHelpers.getMimeType(
        this.fileItem.fileType,
        GeneralHelpers.fileExtensionFromString(this.fileItem.name)
      )
    });

    GeneralHelpers.blobToBase64(blob).then((base64Data: any) => {
      const mimeType = GeneralHelpers.getMimeType(
        this.fileItem.fileType,
        GeneralHelpers.fileExtensionFromString(this.fileItem.name)
      );
      this.base64.set(`data:${mimeType};base64,${base64Data}`);
    }).catch((_error) => {
      this.toastr.error('Failed to process file data');
    });
  }

  private setFileType() {
    const name = this.data?.dialogData?.name;
    if (name !== undefined) {
      this.fileType = GeneralHelpers.fileExtensionFromString(name);
    }
  }

  private setMode() {
    this.mode = (CODEMIRROR_TYPE_MATCH as any)[this.fileType];
  }

  public updateContent(data: any) {
    this.data.model = data;
  }

  /**
   * Placeholder method for form validation.
   * Called when form fields need to be marked as touched.
   * Currently not implemented as form validation is handled elsewhere.
   */
  public setFormTouched(): void {
    // Intentionally empty - form validation handled elsewhere
  }

  private parseCsvFile(file: any) {
    const config = {
      header: true,
      delimiter: '', // auto-detect
      complete: (response: any) => {
        this.tableData.set(GeneralHelpers.addTableColumns(response.data));
      },
      error: (error: any) => {
        this.toastr.error(error.message);
      },
      download: false,
      skipEmptyLines: 'greedy',
      chunk: undefined,
      fastMode: undefined,
      withCredentials: undefined,
    };

    this.papa.parse(file, config as any);
  }
}
